import React, { useMemo, useState } from "react";
import { ListColumn } from "../Datalist/Datalist";
import Datalist from "../Datalist/Datalist";
import { format } from "date-fns";
import { Modal, Spinner } from "react-bootstrap";
import { FaCheckSquare, FaSquare } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faCheck, faXmark, faPen } from "@fortawesome/free-solid-svg-icons";
import "./MemberTable.scss";

export interface ManualMemberRow {
  selected: boolean;
  customerOrgId: string;
  customerName: string;
  contactPerson: string;
  lastReviewed: string;
}

type Props = {
  memberData: ManualMemberRow[];
  isLoading: boolean;

  onCheckboxChange: (orgId: string) => void;

  // CRUD callbacks (kopplade till backend via dataservice från sidan)
  onUpdate: (customerOrgId: string, payload: { customerName: string; contactPerson: string }) => Promise<boolean>;
  onDelete: (customerOrgId: string) => Promise<boolean>;

  // för att kunna uppdatera local state vid typing (sidan äger state)
  onLocalChange: (orgId: string, patch: Partial<Pick<ManualMemberRow, "customerName" | "contactPerson">>) => void;
};

const ManualMemberTable: React.FC<Props> = ({
  memberData,
  isLoading,
  onCheckboxChange,
  onUpdate,
  onDelete,
  onLocalChange,
}) => {
  const [editingOrgId, setEditingOrgId] = useState<string | null>(null);
  const [savingOrgId, setSavingOrgId] = useState<string | null>(null);

  const [confirmDeleteOrgId, setConfirmDeleteOrgId] = useState<string | null>(null);
  const [deletingOrgId, setDeletingOrgId] = useState<string | null>(null);

  const startEdit = (orgId: string) => setEditingOrgId(orgId);
  const cancelEdit = () => setEditingOrgId(null);

  const saveEdit = async (orgId: string) => {
    const row = memberData.find((m) => m.customerOrgId === orgId);
    if (!row) return;

    setSavingOrgId(orgId);
    try {
      const ok = await onUpdate(orgId, {
        customerName: row.customerName,
        contactPerson: row.contactPerson,
      });
      if (ok) setEditingOrgId(null);
    } finally {
      setSavingOrgId(null);
    }
  };

  const columns: ListColumn<ManualMemberRow>[] = useMemo(() => {
    const cols: ListColumn<ManualMemberRow>[] = [
      {
        key: "selected",
        fieldName: "selected",
        name: "",
        minWidth: "4%",
        maxWidth: "4%",
        isActive: true,
        sorting: false,
        onRender: (item) => (
          <div
            className="checkbox-container"
            onClick={() => {
              if (item.contactPerson !== "Epost saknas") onCheckboxChange(item.customerOrgId);
            }}
          >
            {item.contactPerson === "Epost saknas" ? (
              <FaSquare className="custom-checkbox disabled" />
            ) : item.selected ? (
              <FaCheckSquare className="custom-checkbox checked" />
            ) : (
              <FaSquare className="custom-checkbox" />
            )}
          </div>
        ),
        footer: (items) => `Totalt antal rader: ${items.length}`,
      },
      {
        key: "customerOrgId",
        fieldName: "customerOrgId",
        name: "Kund",
        minWidth: "28%",
        maxWidth: "28%",
        isActive: true,
        sorting: true,
        onRender: (item) => `${item.customerName} - ${item.customerOrgId}`,
      },
      {
        key: "contactPerson",
        fieldName: "contactPerson",
        name: "Kontakt Person",
        minWidth: "28%",
        maxWidth: "28%",
        isActive: true,
        sorting: true,
        onRender: (item) => {
          const isEditing = editingOrgId === item.customerOrgId;
          return isEditing ? (
            <input
              type="email"
              value={item.contactPerson}
              onChange={(e) => onLocalChange(item.customerOrgId, { contactPerson: e.target.value })}
              className="editable-contact-email"
              style={{ border: "1px solid #ced4da", padding: "5px", width: "100%" }}
              disabled={savingOrgId === item.customerOrgId}
            />
          ) : (
            <span className={item.contactPerson === "Epost saknas" ? "missing-email" : ""}>
              {item.contactPerson}
            </span>
          );
        },
      },
      {
        key: "lastReviewed",
        fieldName: "lastReviewed",
        name: "Senast Kontaktad",
        minWidth: "22%",
        maxWidth: "22%",
        isActive: true,
        sorting: true,
        onRender: (item) => {
          const d = new Date(item.lastReviewed);
          return isNaN(d.getTime()) ? "N/A" : format(d, "yyyy-MM-dd");
        },
      },
      {
        key: "actions",
        fieldName: "actions",
        name: "",
        minWidth: "18%",
        maxWidth: "18%",
        isActive: true,
        sorting: false,
        onRender: (item) => {
          const isEditing = editingOrgId === item.customerOrgId;
          const isSaving = savingOrgId === item.customerOrgId;
          return (
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              {isEditing ? (
                <>
                  <button
                    className="btn"
                    style={{ padding: "6px 10px" }}
                    onClick={() => saveEdit(item.customerOrgId)}
                    disabled={isSaving}
                    title="Spara"
                  >
                    {isSaving ? <Spinner as="span" size="sm" animation="border" /> : <FontAwesomeIcon icon={faCheck} />}
                  </button>
                  <button
                    className="btn button-cancel"
                    style={{ padding: "6px 10px" }}
                    onClick={cancelEdit}
                    disabled={isSaving}
                    title="Avbryt"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn"
                    style={{ padding: "6px 10px" }}
                    onClick={() => startEdit(item.customerOrgId)}
                    title="Redigera"
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <button
                    className="btn button-cancel"
                    style={{ padding: "6px 10px" }}
                    onClick={() => setConfirmDeleteOrgId(item.customerOrgId)}
                    title="Ta bort"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </>
              )}
            </div>
          );
        },
      },
    ];

    return cols;
  }, [memberData, editingOrgId, savingOrgId, onCheckboxChange, onLocalChange]);

  return (
    <>
      <div className="member-table-wrapper">
        <Datalist
          items={memberData}
          isLoading={isLoading}
          columns={columns}
          setColumns={() => {}}
          initialSortKey="lastReviewed"
          descendingSort={true}
          noItemsText="Inga manuella företag tillgängliga"
          uniqueKey="customerOrgId"
        />
      </div>

      <Modal show={confirmDeleteOrgId != null} onHide={() => setConfirmDeleteOrgId(null)} centered>
        <Modal.Header closeButton style={{backgroundColor:'#173c53',color:'white'}}>
          <Modal.Title>Bekräfta borttagning</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{backgroundColor:'#fffaf5'}}>Är du säker på att du vill ta bort företaget?</Modal.Body>
        <Modal.Footer style={{backgroundColor:'#fffaf5'}}>
          <button className="btn button-cancel" onClick={() => setConfirmDeleteOrgId(null)} disabled={deletingOrgId != null}>
            Avbryt
          </button>
          <button
            className="btn"
            onClick={async () => {
              if (!confirmDeleteOrgId) return;
              setDeletingOrgId(confirmDeleteOrgId);
              try {
                await onDelete(confirmDeleteOrgId);
              } finally {
                setDeletingOrgId(null);
                setConfirmDeleteOrgId(null);
              }
            }}
            disabled={deletingOrgId != null}
          >
            {deletingOrgId ? <Spinner as="span" size="sm" animation="border" /> : "Bekräfta"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ManualMemberTable;
